import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Target, 
  TestTube, 
  Layers, 
  Factory, 
  FileText, 
  HardHat, 
  Pill,
  Home as HomeIcon 
} from 'lucide-react';

const IndustriesPage = () => {
  const industries = [
    {
      icon: Target,
      name: 'Paint & Coatings',
      description: 'Calcite powder and PCC for opacity, whiteness, and cost optimization in decorative and industrial paints.',
      products: ['Calcite Powder', 'Precipitated Calcium Carbonate']
    },
    {
      icon: Layers,
      name: 'Plastic & Polymer',
      description: 'Calcium carbonates and fillers for PVC pipes, profiles, and polymer compounds to improve mechanical properties.',
      products: ['Calcite Powder', 'PCC', 'Starch']
    },
    {
      icon: Factory,
      name: 'Rubber',
      description: 'Reinforcing fillers and processing aids for tire manufacturing and industrial rubber products.',
      products: ['Calcite Powder', 'Precipitated Calcium Carbonate']
    },
    {
      icon: FileText,
      name: 'Paper',
      description: 'PCC and starch for paper coating, filling, and sizing applications to enhance printability and brightness.',
      products: ['PCC', 'Starch', 'Calcite Powder']
    },
    {
      icon: HardHat,
      name: 'Construction',
      description: 'Bentonite for waterproofing, drilling fluids, and calcium carbonate for cement and mortar formulations.',
      products: ['Bentonite', 'Calcite Powder']
    },
    {
      icon: TestTube,
      name: 'Animal Feed',
      description: 'Dicalcium phosphate and tricalcium phosphate as essential mineral supplements for poultry, cattle, and aqua feed.',
      products: ['DCP', 'TCP', 'Calcite Powder']
    },
    {
      icon: Pill,
      name: 'Pharmaceutical',
      description: 'USP/BP grade calcium phosphates and carbonates as excipients in tablet formulations and dietary supplements.',
      products: ['TCP', 'DCP', 'PCC']
    },
    {
      icon: HomeIcon,
      name: 'Textiles',
      description: 'Modified starch for textile sizing, finishing, and printing applications to improve fabric strength and appearance.',
      products: ['Starch']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Industries We Serve</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Supplying critical raw materials across major industrial sectors with technical expertise and reliability
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-600">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100">
                          <Icon className="w-8 h-8 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h2 className="text-2xl font-bold text-slate-900 mb-3">{industry.name}</h2>
                        <p className="text-gray-700 mb-4 leading-relaxed">{industry.description}</p>
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-2 text-sm uppercase">Key Products:</h3>
                          <div className="flex flex-wrap gap-2">
                            {industry.products.map((product, idx) => (
                              <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded text-sm font-medium">
                                {product}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Expertise */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">Our Industry Expertise</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <Card className="border-2 text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-blue-600 mb-2">8+</div>
                  <p className="text-gray-700 font-semibold">Industries Served</p>
                </CardContent>
              </Card>

              <Card className="border-2 text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-blue-600 mb-2">6</div>
                  <p className="text-gray-700 font-semibold">Product Categories</p>
                </CardContent>
              </Card>

              <Card className="border-2 text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
                  <p className="text-gray-700 font-semibold">Quality Assured</p>
                </CardContent>
              </Card>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Technical Support</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our team provides application-specific guidance for product selection, dosage optimization, 
                and process integration. We work closely with your technical teams to ensure material 
                specifications align with your manufacturing requirements.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Product grade recommendations based on end-application</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Technical datasheets and certifications</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Sample provision for trial batches</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Ongoing technical consultation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Partner With Us for Your Industry</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Get customized solutions and reliable supply for your specific industrial requirements
          </p>
          <a href="/contact" className="inline-block">
            <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded text-lg font-semibold transition-colors">
              REQUEST INDUSTRY-SPECIFIC QUOTE
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
